import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Brain, Check, X, BookOpen, Sparkles } from "lucide-react";

/**
 * Overlay surfaces for the gaseous-state deck — concept checks, MCQ,
 * subjective, drag-drop, recap, and real-life example.
 *
 * All overlays are READ-ONLY on the student board. The teacher panel drives
 * which overlay is visible via the `overlay` prop:
 *
 *   { type: "example", payload: { example } }
 *   { type: "mcq",     payload: { mcq, revealAnswer, selectedId } }
 *   { type: "subj",    payload: { q, revealExpected, revealMistake } }
 *   { type: "activity", payload: { activity, placements, checkResult, showCorrect } }
 *   { type: "recap",   payload: { recap } }
 *   null  → no overlay
 */
export const GaseousOverlay = ({ overlay }) => {
    return (
        <AnimatePresence>
            {overlay && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-40 flex items-center justify-center px-12 py-10 backdrop-blur-2xl bg-white/30"
                    data-testid={`gaseous-overlay-${overlay.type}`}
                >
                    <motion.div
                        initial={{ scale: 0.96, y: 12, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.96, y: 12, opacity: 0 }}
                        transition={{ duration: 0.22 }}
                        className="w-full max-w-5xl max-h-full overflow-y-auto rounded-3xl bg-white soft-shadow border border-black/[0.04]"
                    >
                        {overlay.type === "example" && (
                            <ExampleView ex={overlay.payload.example} />
                        )}
                        {overlay.type === "mcq" && (
                            <MCQView {...overlay.payload} />
                        )}
                        {overlay.type === "subj" && (
                            <SubjView {...overlay.payload} />
                        )}
                        {overlay.type === "activity" && (
                            <ActivityView {...overlay.payload} />
                        )}
                        {overlay.type === "recap" && (
                            <RecapView recap={overlay.payload.recap} />
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const Pill = ({ children, tone = "primary" }) => {
    const tones = {
        primary: "bg-[#E0F4F4] text-[#2E8E9A]",
        warm: "bg-[#FFF1E5] text-[#C75730]",
    };
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-semibold ${tones[tone]}`}
        >
            {children}
        </span>
    );
};

const ExampleView = ({ ex }) => (
    <div className="grid grid-cols-12 gap-0">
        <div className="col-span-12 md:col-span-5 aspect-[4/3] md:aspect-auto overflow-hidden">
            <img
                src={ex.image}
                alt={ex.headline}
                className="w-full h-full object-cover"
            />
        </div>
        <div className="col-span-12 md:col-span-7 p-10 flex flex-col justify-center">
            <Pill tone="warm">
                <BookOpen className="w-3 h-3" />
                Real-life example
            </Pill>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mt-4 leading-tight">
                {ex.headline}
            </h2>
            <p className="text-lg text-[#4a5662] mt-4 leading-relaxed">
                {ex.description}
            </p>
        </div>
    </div>
);

const MCQView = ({ mcq, revealAnswer, selectedId }) => (
    <div className="p-10">
        <Pill>
            <Brain className="w-3 h-3" />
            Concept check
        </Pill>
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mt-4 leading-snug">
            {mcq.stem}
        </h2>
        <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-3">
            {mcq.options.map((opt, i) => {
                const isCorrect = !!opt.correct;
                const isSel = selectedId === opt.id;
                let cls =
                    "border-2 border-black/[0.06] bg-white text-[#14202a]";
                if (revealAnswer && isCorrect)
                    cls =
                        "border-2 border-[#5BC2CC] bg-[#E0F4F4] text-[#14202a]";
                else if (revealAnswer && isSel && !isCorrect)
                    cls =
                        "border-2 border-[#FFA987] bg-[#FFF1E5] text-[#7A3A20]";
                else if (isSel) cls = "border-2 border-[#5BC2CC] bg-white";
                return (
                    <div
                        key={opt.id}
                        data-testid={`gaseous-mcq-option-${opt.id}`}
                        className={`px-5 py-4 rounded-2xl flex items-start gap-3 ${cls}`}
                    >
                        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#4a5662] mt-1">
                            {String.fromCharCode(65 + i)}
                        </span>
                        <span className="text-base leading-snug flex-1">
                            {opt.text}
                        </span>
                        {revealAnswer && isCorrect && (
                            <Check className="w-5 h-5 text-[#2E8E9A] mt-1" />
                        )}
                        {revealAnswer && isSel && !isCorrect && (
                            <X className="w-5 h-5 text-[#C75730] mt-1" />
                        )}
                    </div>
                );
            })}
        </div>
        {revealAnswer && mcq.explain && (
            <div className="mt-6 px-5 py-4 rounded-2xl bg-[#F5FAFC] border border-black/[0.04]">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-1.5">
                    Why this is the answer
                </p>
                <p className="text-[15px] text-[#14202a] leading-snug">
                    {mcq.explain}
                </p>
            </div>
        )}
    </div>
);

const SubjView = ({ q, revealExpected, revealMistake }) => (
    <div className="p-10">
        <Pill tone="warm">
            <Sparkles className="w-3 h-3" />
            Practice question
        </Pill>
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mt-4 leading-snug">
            {q.stem}
        </h2>
        {revealExpected && (
            <div className="mt-6">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#2E8E9A] font-semibold mb-2">
                    Expected answer
                </p>
                <ul className="space-y-2">
                    {q.expected.map((line, i) => (
                        <li
                            key={i}
                            className="flex items-start gap-3 text-[15px] leading-snug"
                        >
                            <span className="mt-2 w-1.5 h-1.5 rounded-full sparkl-gradient shrink-0" />
                            {line}
                        </li>
                    ))}
                </ul>
            </div>
        )}
        {revealMistake && q.commonMistake && (
            <div className="mt-6 px-5 py-4 rounded-2xl bg-[#FFF1E5] border border-[#FFA987]/40">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#C75730] font-semibold mb-1.5">
                    Watch for — common mistake
                </p>
                <p className="text-[15px] text-[#7A3A20] leading-snug">
                    {q.commonMistake}
                </p>
            </div>
        )}
    </div>
);

const ActivityView = ({ activity, placements, checkResult, showCorrect }) => {
    const placementsMap = placements || {};
    const itemsInCategory = (catId) =>
        activity.items.filter((it) => placementsMap[it.id] === catId);
    const unplaced = activity.items.filter((it) => !placementsMap[it.id]);
    return (
        <div className="p-10">
            <Pill tone="warm">
                <Sparkles className="w-3 h-3" />
                Activity
            </Pill>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mt-4 leading-snug">
                {activity.title}
            </h2>
            <p className="text-[14px] text-[#4a5662] mt-2">
                {activity.instruction}
            </p>

            {/* Buckets */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
                {activity.categories.map((cat) => (
                    <div
                        key={cat.id}
                        className="rounded-2xl bg-[#F5FAFC] border-2 border-dashed border-[#B8E8E8] p-4 min-h-[140px]"
                        data-testid={`gaseous-activity-bucket-${cat.id}`}
                    >
                        <p className="text-[10px] uppercase tracking-[0.22em] text-[#2E8E9A] font-semibold mb-3">
                            {cat.label}
                        </p>
                        <div className="space-y-2">
                            {itemsInCategory(cat.id).map((it) => {
                                const correct = it.correct === cat.id;
                                let tone = "bg-white text-[#14202a]";
                                if (checkResult || showCorrect) {
                                    tone = correct
                                        ? "bg-[#E0F4F4] text-[#14202a] border border-[#5BC2CC]"
                                        : "bg-[#FFF1E5] text-[#7A3A20] border border-[#FFA987]";
                                }
                                return (
                                    <div
                                        key={it.id}
                                        className={`px-3 py-2 rounded-xl text-[13px] font-medium ${tone} border border-black/[0.04]`}
                                    >
                                        {it.text}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* Tray (unplaced) */}
            {unplaced.length > 0 && (
                <div className="mt-5">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-2">
                        Drag from here
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {unplaced.map((it) => (
                            <div
                                key={it.id}
                                className="px-3 py-2 rounded-xl bg-white border border-black/[0.04] text-[13px] font-medium text-[#14202a]"
                            >
                                {it.text}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {checkResult && (
                <div className="mt-5 px-5 py-3 rounded-2xl bg-[#F5FAFC] border border-black/[0.04]">
                    <p className="text-[13px] text-[#14202a]">
                        Got{" "}
                        <span className="font-semibold text-[#2E8E9A]">
                            {checkResult.correct}
                        </span>{" "}
                        out of {checkResult.total} right.
                    </p>
                </div>
            )}
        </div>
    );
};

const RecapView = ({ recap }) => (
    <div className="p-10">
        <Pill>
            <BookOpen className="w-3 h-3" />
            Recap
        </Pill>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mt-4 leading-tight">
            {recap.title}
        </h2>
        <ul className="mt-7 space-y-4">
            {recap.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-4">
                    <span className="mt-2 w-3 h-3 rounded-full sparkl-gradient shrink-0 shadow-[0_0_20px_rgba(91,194,204,0.4)]" />
                    <p className="text-[17px] text-[#14202a] leading-snug">
                        {b}
                    </p>
                </li>
            ))}
        </ul>
    </div>
);

export default GaseousOverlay;
